import { FC, useEffect } from 'react';
import { Form } from 'rsuite';
import _ from 'lodash';
import moment from 'moment';

import { Section } from 'components/Rsuite/Section';
import { RsInput } from 'components/Rsuite/Input';
import { RsButton } from 'components/Rsuite/Button';
import { RsConfirmationModalTrigger } from 'components/Rsuite/Modal';

import useKeywordForm from 'containers/RtbCampaigns/RtbCampaignSinglePage/Hooks/useKeywordForm';
import useBaseForm from 'containers/RtbCampaigns/RtbCampaignSinglePage/Hooks/useBaseForm';

import { useCampaignStore } from 'store/campaignStore';
import { useOrderStore } from 'store/orderStore';
import Outside from './Keyword/Outbound/Outbound';
import { BidMode } from 'core/rtbCampaign/singlePage/keyword';
import { SelectPattern } from 'core/rtbCampaign/singlePage/common';
import { ProductSelectMode } from './Common/ProductSelectionMode/ProductSelectMode';
import { RunningPatternMode } from './Common/RunningPatternMode/RunningPatternMode';
import { BudgetMode } from './Common/BudgetChoiceMode/BudgetMode';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { BidTypeSelection } from './Keyword/BidTypeSelection';
import styles from './baseForm.module.scss';
import { useFormik, yupToFormErrors } from 'formik';
import useKeywordValidation from '../Hooks/useKeywordValidation';
import {
  DefaultRtbCampaignManager,
  RtbCampaignManager,
} from 'core/rtbCampaign/RtbCampaignManager';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import useOutboundMeta from '../Hooks/useOutboundMeta';
import ProductManager from 'components/Rsuite/ProductManager/ProductManager';
import { useOutboundState } from './Common/useOutboundState';
interface KeywordFormProps {
  defaultForm: any;
}
const campaignManager: RtbCampaignManager = new DefaultRtbCampaignManager();

const KeywordForm: FC<KeywordFormProps> = ({ defaultForm }) => {
  const history = useHistory();
  const { order } = useOrderStore();
  const { actionType, loading, roasList, handleLoading } = useCampaignStore();
  const preprocessDefaultForm =
    actionType === 'copy'
      ? {
          ...defaultForm,
          basic: {
            ...defaultForm.basic,
            outboundInfo: {
              brandNo: '',
              brandName: '',
              name: '',
              logoUrl: '',
              goal: '',
              adContent: '',
            },
          },
        }
      : defaultForm;
  // init Formik
  const { values, setFieldValue, setErrors, handleSubmit } = useFormik({
    initialValues: { ...defaultForm },
    onSubmit: async (values) => {
      const campaign = {
        ...values,
        products:
          values.basic.selectPattern === SelectPattern.AUTO
            ? selectedAutoProducts
            : selectedProducts,
        limitations:
          values.basic.bidMode === BidMode.AUTO
            ? values.limitations
            : {
                include: [
                  {
                    op: '',
                    type: 'searchKeywords',
                    value: values.keywords.map((keyword) => ({
                      value: keyword.keyword,
                      label: keyword.keyword,
                      isError: false,
                    })),
                  },
                ],
              },
      };
      // return
      validationSchema
        .validate(values, { abortEarly: false })
        .then(async function () {
          handleLoading(true);
          try {
            switch (actionType) {
              case 'edit':
                await campaignManager.updateCampaign(
                  campaign,
                  order.l1Objects[0]?.l1ObjectId,
                );
                toast.success('成功');

                history.push(`/orders/${order?.orderNumber}`);
                break;
              case 'create':
                await campaignManager.createCampaign(
                  campaign,
                  order.l1Objects[0]?.l1ObjectId,
                );
                toast.success('成功');
                history.push(`/orders/${order?.orderNumber}`);
                break;
              case 'copy':
                await campaignManager.createCampaign(
                  campaign,
                  order.l1Objects[0]?.l1ObjectId,
                );
                toast.success('成功');
                history.push(`/orders/${order?.orderNumber}`);
                break;
              default:
                break;
            }
          } catch (error) {
            console.error('Failed to create campaign:', error);
            handleLoading(false);
          }
        })
        .catch(function (err) {
          setErrors(yupToFormErrors(err));
          console.log('errors', yupToFormErrors(err));
        });
    },
  });

  // 其他代碼...

  // 提交表單
  const onSubmit = () => {
    handleSubmit();
  };
  const { campaignMinDate, handleCampaignNameChange, handleBack } = useBaseForm(
    {
      values,
      setFieldValue,
    },
  );

  const {
    brandOptions,
    suggestKeywords,
    diffDays,
    selectBrand,
    selectedAutoProducts,
    selectedProducts,
    handleSelectPatternChange,
    handleBrandChange,
    handleDurationChange,
    handleDateRangeChange,
    handleChangeStartDate,
    handleChangeEndDate,
    handleBudgetTypeChange,
    handleAlwaysOnBudgetChange,
    handleBidTypeChange,
    handleRoasStrategyChange,
    handleCustomRoasChange,
    handleManualKeywordsChange,
  } = useKeywordForm({
    defaultForm,
    values,
    setFieldValue,
  });

  const {
    didFetch,
    outboundBrandOptions,
    enAbledOutboundSection,
    outboundHiteText,
    outsideMetaPermission,
    handleChangOutbound,
    handleSetMetaContent,
    handleChangeToggle,
    handleSegmentChange,
    handleGoalChange,
    handleCloseProductManager,
    limitedOutbound,
  } = useOutboundMeta({
    defaultForm: preprocessDefaultForm,
    values,
    diffDays,
    selectBrand,
    setFieldValue,
  });

  // 使用 useOutboundState 為三個不同的地方生成 disabled 和 disabledText

  const {
    disabled: runningPatternDisabled,
    disabledText: runningPatternDisabledText,
  } = useOutboundState(
    'runningPattern',
    actionType,
    defaultForm.basic?.outboundInfo,
    { runningPattern: defaultForm.basic.runningPattern },
  );

  const { disabled: budgetDisabled, disabledText: budgetDisabledText } =
    useOutboundState('budget', actionType, defaultForm.basic?.outboundInfo, {
      budgetType: values.basic.budgetType,
    });

  const {
    disabled: productSelectionDisabled,
    disabledText: productSelectionDisabledText,
  } = useOutboundState(
    'productSelection',
    actionType,
    defaultForm.basic?.outboundInfo,
    {
      brandOptions: brandOptions,
      selectPattern: values.basic.selectPattern,
    },
  );

  const { validationSchema, errors, handleValidation } = useKeywordValidation({
    defaultForm,
    newForm: values,
  });

  useEffect(() => {
    handleValidation();
  }, [values, roasList]);

  return (
    <>
      {loading && <LoadingIndicator />}
      <Section title='廣告活動基本設定' className={'min-h-[580px]'}>
        <Form layout='horizontal' className='w-[1340px]'>
          <Form.Group>
            <Form.ControlLabel>活動名稱</Form.ControlLabel>
            <RsInput
              value={values.basic?.name}
              onChange={handleCampaignNameChange}
              className='!w-80'
              errorMessage={errors?.basic?.name}
            />
          </Form.Group>
          <Form.Group>
            <Form.ControlLabel>商品設定</Form.ControlLabel>
            {values.basic.bidMode === BidMode.MANUAL ? (
              <RsConfirmationModalTrigger
                message='將清空手動出價與選字'
                renderModalTrigger={(showModal) => (
                  <ProductSelectMode
                    defaultForm={preprocessDefaultForm}
                    closeProductManager={handleCloseProductManager}
                    selectPattern={values.basic.selectPattern}
                    brandOptions={brandOptions}
                    onSelectPatternChange={showModal}
                    brand={selectBrand}
                    onBrandChange={handleBrandChange}
                    errorMessage={errors}
                    disabled={productSelectionDisabled}
                    disabledText={productSelectionDisabledText}
                    availableProducts={25}
                  />
                )}
                onConfirm={(closeModal) => {
                  handleSelectPatternChange();
                  closeModal();
                }}
              />
            ) : (
              <ProductSelectMode
                defaultForm={preprocessDefaultForm}
                closeProductManager={handleCloseProductManager}
                selectPattern={values.basic.selectPattern}
                brandOptions={brandOptions}
                onSelectPatternChange={handleSelectPatternChange}
                brand={selectBrand}
                onBrandChange={handleBrandChange}
                errorMessage={errors}
                disabled={productSelectionDisabled}
                disabledText={productSelectionDisabledText}
                availableProducts={25}
              />
            )}
            <ProductManager
              closeProductManager={handleCloseProductManager}
              availableProducts={25}
              showImportButton={true}
            />
          </Form.Group>
          <Form.Group controlId='data-range-radio'>
            <Form.ControlLabel>廣告期間</Form.ControlLabel>
            <RunningPatternMode
              selected={values.basic.runningPattern}
              diffDays={diffDays}
              defaultRunningPattern={preprocessDefaultForm.basic.runningPattern}
              runningPattern={values.basic.runningPattern}
              onChange={handleDurationChange}
              onChangeStartDate={handleChangeStartDate}
              onChangeEndDate={handleChangeEndDate}
              startDate={values.basic.startDate}
              endDate={values.basic.endDate}
              onDateRangeChange={handleDateRangeChange}
              dataRangeError={[errors?.basic?.startDate, errors?.basic?.endDate]
                .filter(Boolean)
                .join(' / ')}
              className={styles.duration}
              minDate={moment(campaignMinDate).format('YYYY-MM-DD HH:mm:ss')}
              maxDate={moment(order.endDate)
                .endOf('day')
                .format('YYYY-MM-DD HH:mm:ss')}
              startDatePickerDisabled={
                actionType === 'edit' &&
                new Date(preprocessDefaultForm.basic.startDate) <= new Date()
              }
              disabled={runningPatternDisabled}
              disabledText={runningPatternDisabledText}
            />
          </Form.Group>
          <Form.Group controlId='data-range-radio'>
            <Form.ControlLabel>廣告預算</Form.ControlLabel>
            <BudgetMode
              budgetType={values.basic.budgetType}
              onBudgetTypeChange={handleBudgetTypeChange}
              budget={values.basic.budget}
              dailyTargetBudget={values.basic.dailyTargetBudget}
              onBudgetChange={handleAlwaysOnBudgetChange}
              runningPattern={values.basic.runningPattern}
              diffDays={diffDays}
              numberOfProducts={
                values?.basic?.selectPattern === SelectPattern.AUTO
                  ? selectedAutoProducts?.length
                  : selectedProducts?.length
              }
              dailyErrorMessage={errors?.basic?.dailyTargetBudget}
              totalErrorMessage={errors?.basic?.budget}
              disabled={budgetDisabled}
              disabledText={budgetDisabledText}
            />
          </Form.Group>
        </Form>
      </Section>
      <Section title='出價設定'>
        <Form layout='horizontal' className='w-[1260px]'>
          <Form.Group>
            <Form.ControlLabel>出價選擇</Form.ControlLabel>
            <div className='flex flex-col gap-1 pt-[10px]'>
              <Form.HelpText className='ml-[8px] !text-sm'>
                活動建立後，將不可於 自動出價與選字/手動出價與選字 模式間切換
              </Form.HelpText>
              <BidTypeSelection
                selectPattern={values?.basic?.selectPattern}
                bidMode={values.basic.bidMode}
                onBidTypeChange={handleBidTypeChange}
                roasStrategy={values.basic.roasOption?.roasStrategy}
                roas={values?.basic?.roasOption?.roas}
                onRoasStrategyChange={handleRoasStrategyChange}
                onCustomRoasChange={handleCustomRoasChange}
                manualKeywords={values.keywords}
                onManualKeywordsChange={handleManualKeywordsChange}
                suggestKeywords={suggestKeywords}
                roasError={errors?.basic?.roasOption?.roas}
                keywordErrors={
                  Array.isArray(errors?.keywords) ? errors.keywords : undefined
                }
                manualError={
                  _.isString(errors?.keywords) ? errors.keywords : undefined
                }
              />
            </div>
          </Form.Group>
        </Form>
      </Section>

      {/* 站外聯播網 */}
      {outsideMetaPermission && (
        <Outside
          outsideMetaPermission={outsideMetaPermission}
          enableOutbound={values?.basic?.enableOutbound}
          enAbledOutboundSection={enAbledOutboundSection}
          outboundHiteText={outboundHiteText}
          outboundBrandOptions={outboundBrandOptions}
          defaultForm={preprocessDefaultForm}
          changeAdContent={handleSetMetaContent}
          webBrandNo={values?.basic?.webBrandNo}
          selectPattern={values?.basic?.selectPattern}
          outboundInfo={values?.basic?.outboundInfo}
          outBoundMetaLoading={!didFetch}
          changOutbound={handleChangOutbound}
          brandNoMessage={errors?.basic?.outboundInfo?.brandNo}
          goalMessage={errors?.basic?.outboundInfo?.goal}
          adContentMessage={errors?.basic?.outboundInfo?.adContent}
          handleChangeToggle={handleChangeToggle}
          handleSegmentChange={handleSegmentChange}
          handleGoalChange={handleGoalChange}
          limitedOutbound={limitedOutbound}
        />
      )}

      <div className='my-8 ml-8 flex items-center gap-4'>
        <RsButton
          appearance='primary'
          onClick={onSubmit}
          disabled={!_.isEmpty(errors)}
        >
          完成
        </RsButton>
        <RsButton appearance='ghost' onClick={handleBack}>
          取消
        </RsButton>
      </div>
    </>
  );
};

export default KeywordForm;
